import { useEffect, useId, useMemo, useRef, useState } from "react";
import { Pencil, Sparkles } from "lucide-react";
import { ApiError, extractErrorMessage } from "@/api/client";
import type {
  BiographyFieldKey,
  BiographyFieldValues,
  BuyerLeadBiographyDto,
  UpsertBuyerLeadBiographyPayload,
} from "@/api/buyers";
import {
  Button,
  StatusBadge,
  Textarea,
  Timestamp,
  useToast,
} from "@/design-system/components/ui";
// Modal is a staff-shell component, not part of the shared portal kit.
import { Modal } from "@/components/ui/Modal";
import {
  useAnalyseBuyerBiography,
  useSaveBuyerBiography,
} from "@/hooks/buyers/useBuyerBiography";
import { DetailSection } from "./detailBits";
import { BIOGRAPHY_FIELDS } from "./biographyFields";
import {
  BIOGRAPHY_ANALYSE_FAILED_MESSAGE,
  BIOGRAPHY_EMPTY_MESSAGE,
  BIOGRAPHY_NOTHING_FOUND_MESSAGE,
  BIOGRAPHY_SAVE_ANNOUNCEMENTS,
} from "./biographyCopy";

/**
 * The buyer's biography — a DOCUMENT to read, with a pencil on each part of it.
 *
 * <p>🔴 <b>A populated biography is something a specialist READS, and it used to
 * render as fourteen open textareas.</b> Thirteen structured fields plus the
 * source text, all editable all the time, on a record most people open to find
 * out what a buyer has done rather than to type. Nothing on the screen
 * distinguished a field somebody had written from one nobody had, because an
 * empty textarea and an unanswered question look identical — so the panel could
 * not answer "what do we know about this buyer" at a glance, which is the only
 * question it exists to answer.</p>
 *
 * <p>Now: a read view, grouped into five parts, each with its own pencil in the
 * <c>DetailSection</c> header — the affordance <c>QualificationPanel</c> and the
 * Notes tab already use. An unanswered field says so in muted prose rather than
 * offering an empty box.</p>
 *
 * <p>🔴 <b>Analysing saves no field.</b> See {@link BiographyPasteModal} — the
 * extraction lands in a form the specialist reads, edits and saves. The one thing
 * an Analyse keeps is a FIRST paste's text, so a failed read or a walk-away does
 * not lose it; on a buyer with a biography, the paste becomes the source text
 * only when somebody saves.</p>
 *
 * <p>🔴 <b>Only a reviewed biography feeds a submission package.</b> The badge
 * says so, because a specialist who pastes, analyses and walks away has produced
 * nothing the package can use, and there is no other place they would find that
 * out.</p>
 */
export function BuyerBiographyPanel({
  buyerLeadId,
  biography,
  canEdit,
}: {
  buyerLeadId: string;
  biography?: BuyerLeadBiographyDto | null;
  canEdit: boolean;
}) {
  const saved = useMemo(() => valuesOf(biography), [biography]);

  const [pasting, setPasting] = useState(false);
  const [editingSection, setEditingSection] = useState<BiographySectionId | null>(null);

  const anythingRecorded = BIOGRAPHY_FIELDS.some((f) => nonEmpty(saved[f.key]));

  return (
    <div className="grid gap-space-4">
      <DetailSection
        title="Biography"
        action={
          canEdit ? (
            // The panel-level act, and deliberately heavier than the five
            // per-section pencils: pasting fills the whole record at once, and a
            // reader must be able to tell that apart from editing one part of it.
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setPasting(true)}
              data-testid="buyer-biography-paste"
            >
              <Sparkles size={14} aria-hidden />
              Paste biography
            </Button>
          ) : null
        }
      >
        <div className="flex flex-wrap items-center gap-space-3">
          {biography?.isReviewed ? (
            <StatusBadge tone="ok">Reviewed</StatusBadge>
          ) : (
            <StatusBadge tone="neutral">Not reviewed</StatusBadge>
          )}
          <span className="text-caption text-gray-mid">
            {biography?.isReviewed ? (
              <>
                Reviewed <Timestamp value={biography.reviewedAtUtc!} />. A submission
                package draws on the biography only once it has been reviewed.
              </>
            ) : (
              <>
                A submission package draws on the biography only once it has been
                reviewed. Saving marks it reviewed.
              </>
            )}
          </span>
        </div>

        {!anythingRecorded && (
          <p className="m-0 mt-space-3 text-small text-gray-mid">
            Nothing recorded yet.
            {canEdit
              ? " Paste a résumé, LinkedIn profile or biography and check what the assistant reads before the biography is saved."
              : ""}
          </p>
        )}
      </DetailSection>

      {BIOGRAPHY_SECTIONS.map((section) => (
        <BiographyReadSection
          key={section.id}
          section={section}
          values={saved}
          onEdit={canEdit ? () => setEditingSection(section.id) : undefined}
        />
      ))}

      {biography?.rawText && <SourceText biography={biography} />}

      <BiographyPasteModal
        buyerLeadId={buyerLeadId}
        open={pasting}
        onClose={() => setPasting(false)}
        saved={saved}
        biography={biography}
      />

      {editingSection && (
        <BiographySectionEditModal
          buyerLeadId={buyerLeadId}
          section={SECTION_BY_ID[editingSection]}
          saved={saved}
          onClose={() => setEditingSection(null)}
        />
      )}
    </div>
  );
}

/* ------------------------------------------------------------ the sections */

type BiographySectionId = "summary" | "experience" | "credentials" | "strengths" | "fit";

interface BiographySection {
  id: BiographySectionId;
  title: string;
  fields: ReadonlyArray<{ key: BiographyFieldKey; label: string }>;
}

/**
 * Which part of the document each field belongs to.
 *
 * <p>🔴 <b>An exhaustive <c>Record</c> over the key union, so a fourteenth field
 * added to <c>BiographyFieldKey</c> is a TYPE ERROR here rather than a field that
 * silently renders nowhere.</b> Grouping by a list-of-lists would compile with a
 * field missing from every group, and the failure — one of thirteen prose blocks
 * quietly absent from a read-only view — is close to undetectable by eye.</p>
 *
 * <p>The ORDER and the LABELS still come from <c>biographyFields.ts</c>, which
 * stays the single authority for both; this file only says which heading each one
 * sits under.</p>
 */
const BIOGRAPHY_SECTION_OF: Record<BiographyFieldKey, BiographySectionId> = {
  professionalSummary: "summary",
  industryExperience: "experience",
  leadershipExperience: "experience",
  operationsExperience: "experience",
  businessOwnershipExperience: "experience",
  education: "credentials",
  certificationsAndLicences: "credentials",
  militaryService: "credentials",
  notableAchievements: "strengths",
  transferableSkills: "strengths",
  geographicTies: "fit",
  acquisitionMotivation: "fit",
  availabilityAndCommitment: "fit",
};

const SECTION_TITLES: Record<BiographySectionId, string> = {
  summary: "Summary",
  experience: "Experience",
  credentials: "Education & credentials",
  strengths: "Strengths",
  fit: "Fit and motivation",
};

const BIOGRAPHY_SECTIONS: readonly BiographySection[] = (
  ["summary", "experience", "credentials", "strengths", "fit"] as const
).map((id) => ({
  id,
  title: SECTION_TITLES[id],
  // Filtered out of BIOGRAPHY_FIELDS rather than re-listed, so the authored
  // order survives and no label is written twice.
  fields: BIOGRAPHY_FIELDS.filter((f) => BIOGRAPHY_SECTION_OF[f.key] === id),
}));

const SECTION_BY_ID: Record<BiographySectionId, BiographySection> = Object.fromEntries(
  BIOGRAPHY_SECTIONS.map((s) => [s.id, s]),
) as Record<BiographySectionId, BiographySection>;

/** One part of the document, read-only, with its own pencil. */
function BiographyReadSection({
  section,
  values,
  onEdit,
}: {
  section: BiographySection;
  values: BiographyFieldValues;
  /** Absent for a reader who may not write — an empty section then states the
   *  fact and offers nothing, rather than a control the server would refuse. */
  onEdit?: () => void;
}) {
  return (
    <DetailSection
      title={section.title}
      action={
        onEdit ? (
          <Button
            variant="text"
            size="sm"
            onClick={onEdit}
            aria-label={`Edit ${section.title}`}
            data-testid={`buyer-biography-edit-${section.id}`}
          >
            <Pencil size={14} aria-hidden />
            Edit
          </Button>
        ) : null
      }
    >
      <div className="grid gap-space-3">
        {section.fields.map((f) => (
          <div key={f.key} className="grid gap-space-1">
            <span className="text-caption uppercase tracking-wide text-gray-mid">
              {f.label}
            </span>
            {/* `whitespace-pre-wrap` because both the paste and the extraction
                carry line breaks a reader put there on purpose. */}
            <p
              className="m-0 whitespace-pre-wrap text-small text-onyx"
              data-testid={`buyer-biography-${f.key}`}
            >
              {nonEmpty(values[f.key]) ? (
                values[f.key]
              ) : (
                <span className="text-gray-mid">Nothing recorded.</span>
              )}
            </p>
          </div>
        ))}
      </div>
    </DetailSection>
  );
}

/**
 * The text everything else was read from.
 *
 * <p>Kept and shown rather than discarded after extraction: a mis-read field is
 * only recoverable if a human can read what the model actually saw. Collapsed,
 * because it is evidence rather than the answer — the same treatment the
 * Original-paste card on the Notes tab gives the buyer's own intake paste.</p>
 */
function SourceText({ biography }: { biography: BuyerLeadBiographyDto }) {
  const [open, setOpen] = useState(false);

  return (
    <DetailSection title="Source text">
      <button
        type="button"
        className="text-small text-gold-shadow hover:underline"
        onClick={() => setOpen((v) => !v)}
      >
        {open ? "Hide the original text" : "Show the original text"}
      </button>
      {open && (
        <pre
          className="mt-space-2 max-h-96 overflow-y-auto whitespace-pre-wrap rounded-md border border-border-light bg-white-warm p-space-3 font-body text-small text-onyx"
          data-testid="buyer-biography-raw"
        >
          {biography.rawText}
        </pre>
      )}

      {/* --- Provenance ---------------------------------------------------

          🔴 `aiModel` is read as a BOOLEAN, never printed. It rendered to a
          specialist as "Last analysed by claude-sonnet-5" — a string
          that tells the reader nothing they can act on, dates the product the
          moment the model changes, and names a vendor in a CRM field for no
          operational reason. `IntroEmailPanel` already draws the same
          distinction the same way ("by the assistant" / "without the
          assistant"), and the sibling Original-paste card dropped the
          identifier for these reasons.

          The provenance itself STAYS: that this was machine-read, when, and
          that the source text is kept, are the three facts a specialist needs
          to check a mis-read field. The value is still on the DTO and still
          stored, so a systematic extraction error remains traceable in the
          data; it simply is not the caption.

          🔴 It lives HERE, under the text it is describing, so "the pasted text
          above" stays literally true. The sentence outlived one restructure
          already; a provenance caption that points at something no longer above
          it is a small lie that costs a reader a search. */}
      {biography.aiModel && (
        <p className="mt-space-3 mb-0 text-caption text-gray-mid">
          Last analysed by the assistant
          {biography.aiAnalysedAtUtc ? (
            <>
              {" "}
              <Timestamp value={biography.aiAnalysedAtUtc} />
            </>
          ) : null}
          . The pasted text above is kept so a mis-read field can always be
          checked against it.
        </p>
      )}
    </DetailSection>
  );
}

/* --------------------------------------------------------------- the modals */

/**
 * ============================================================================
 * THE PASTE → EXTRACT → REVIEW → SAVE PATTERN
 * ============================================================================
 *
 * <p>SEVEN surfaces in this app take a blob of pasted text, send it to an AI for
 * extraction, and land the result in front of a human. They have drifted, and
 * {@link ../PasteToCreateListingModal PasteToCreateListingModal} is the
 * reference implementation — copy its shape, not any of the others'.</p>
 *
 * <p>The seven, named so the count is checkable rather than asserted:
 * <c>PasteToCreateListingModal</c>, <c>PasteToCreateBuyerModal</c>,
 * <c>pages/leads/PasteToCreateLeadModal</c>,
 * <c>presentBuyer/PresentBuyerWizard</c>,
 * <c>calendar/components/AiCreateAppointmentPanel</c>,
 * <c>portal/tabs/leadGen/PreleadsTab</c>, and this one.</p>
 *
 * <p>What makes it the reference, and what every one of these must do:</p>
 *
 * <ol>
 *   <li><b>Every extracted value lands in an ordinary EDITABLE input, so the
 *       review surface IS the extraction.</b> A read-only summary with
 *       Apply / Discard underneath asks the reader to accept or reject thirteen
 *       values they cannot see, which is not a review.</li>
 *   <li><b><c>open</c> is a controlled prop; the host owns the lifecycle.</b>
 *       A modal that owns its own open state cannot be opened from a second
 *       place, and there is always eventually a second place.</li>
 *   <li><b>ONE <c>reset()</c> clears every phase.</b> Reopening after a cancel
 *       must not show the previous paste's answers — and with five pieces of
 *       state cleared at three call sites, one of them eventually is not.</li>
 *   <li><b>A 422 renders the SERVER's message and PRESERVES the paste.</b> The
 *       endpoint raises 422 for every failure it can name — the paste was too
 *       long, it held no biography, the assistant's reply could not be read,
 *       the assistant was unavailable, the daily AI cost ceiling was reached —
 *       and those read completely differently to the person waiting; throwing
 *       away what they pasted makes any of them worse.</li>
 *   <li><b>The model identifier is never published.</b> It tells the reader
 *       nothing they can act on and dates the product the moment it changes.
 *       "Extracted automatically from pasted text" is the whole claim.</li>
 *   <li><b>Nothing is written until the human presses save.</b> This one keeps
 *       a FIRST paste's text on the buyer when it is analysed, so a failed read
 *       or a walk-away does not lose it, and says so on the paste step; no
 *       field is ever written before Save.</li>
 * </ol>
 *
 * <p>The other six are out of scope here and are NOT being refactored.</p>
 *
 * ============================================================================
 *
 * <p>🔴 <b>What this one adds, because it is the only one of the seven that can
 * overwrite existing work.</b></p>
 *
 * <ul>
 *   <li><b>The form starts from the EXTRACTION, never from what is saved.</b>
 *       It used to start from the saved biography with the read laid on top, so
 *       a read that found nothing produced a form identical to the biography on
 *       file — and "Save and mark reviewed" re-saved it under a success toast
 *       while the new paste changed nothing. A field the paste does not mention
 *       now starts EMPTY and shows what is on file under it, with a per-field
 *       "Keep what's saved"; one control above the fields fills every such field
 *       nobody has typed into, once. A value the read would REPLACE names the
 *       saved value it replaces, with the same keep control.</li>
 *   <li><b>A read that fails or finds nothing STAYS ON THE PASTE STEP</b> — red,
 *       with the reason, the paste kept, no Save, and a way to fill the fields
 *       in by hand. A 5xx or a network failure gets one plain sentence rather
 *       than the server's log text. The hand-entry form never claims the
 *       assistant read it, and a save from it carries no analysis.</li>
 *   <li><b>What a save says is the SERVER's account of what it did</b> —
 *       nothing, the source text only, or a field — and an empty biography
 *       cannot be saved.</li>
 * </ul>
 */
function BiographyPasteModal({
  buyerLeadId,
  open,
  onClose,
  saved,
  biography,
}: {
  buyerLeadId: string;
  open: boolean;
  onClose: () => void;
  saved: BiographyFieldValues;
  biography?: BuyerLeadBiographyDto | null;
}) {
  const toast = useToast();
  const analyse = useAnalyseBuyerBiography(buyerLeadId);
  const save = useSaveBuyerBiography(buyerLeadId);
  const emptyReasonId = useId();
  const subjectNoticeId = useId();

  // 🔴 Every read, and every session, is numbered, and a callback acts only on
  // its own. This modal stays mounted when it closes, so a read cancelled
  // mid-flight still lands here — and must not reopen the next paste onto a
  // review of the one the specialist walked away from.
  const attempt = useRef(0);
  // The paste box is seeded ONCE per open. A first paste refreshes the buyer's
  // detail, which moves the source text under the open modal, and a refetch
  // must never overwrite what the specialist is typing.
  const seeded = useRef(false);

  // The paste step.
  const [text, setText] = useState("");
  const [readError, setReadError] = useState<string | null>(null);
  /** THIS session's read is running. Not `analyse.isPending`: the modal stays
   *  mounted when it closes, so a read cancelled mid-flight is still pending
   *  when the next paste opens, and must not lock that paste's box. */
  const [reading, setReading] = useState(false);

  // The form — null until a read succeeds, or the specialist fills it by hand.
  const [mode, setMode] = useState<PasteFormMode | null>(null);
  const [fields, setFields] = useState<BiographyFieldValues>({});
  /** What the read found, so "the paste doesn't mention this" stays answerable
   *  after the specialist has typed over it. */
  const [extracted, setExtracted] = useState<BiographyFieldValues>({});
  /** Typed into — including typed into and then cleared, which is a choice,
   *  not an omission, so the bulk keep leaves it alone. */
  const [touched, setTouched] = useState<ReadonlySet<BiographyFieldKey>>(() => new Set());
  /** Filled from what is saved by a keep control, for the label under it. */
  const [kept, setKept] = useState<ReadonlySet<BiographyFieldKey>>(() => new Set());
  const [provenance, setProvenance] = useState<BiographyProvenance | null>(null);

  const reset = () => {
    attempt.current += 1;
    seeded.current = false;
    setText("");
    setReadError(null);
    setReading(false);
    setMode(null);
    setFields({});
    setExtracted({});
    setTouched(new Set());
    setKept(new Set());
    setProvenance(null);
  };

  const close = () => {
    reset();
    onClose();
  };

  // Seeds the paste box with the text on file, so re-analysing an existing
  // biography does not start by asking the specialist to find it again.
  useEffect(() => {
    if (!open) {
      seeded.current = false;
      return;
    }
    if (seeded.current) return;
    seeded.current = true;
    setText(biography?.rawText ?? "");
  }, [open, biography?.rawText]);

  // A buyer with no biography row yet: the server keeps the text of a paste
  // like this one when it is analysed, and the copy has to say so.
  const firstPaste = !biography?.id;

  const runAnalyse = () => {
    attempt.current += 1;
    const mine = attempt.current;
    // 🔴 The read and the text it read are ONE fact. The box is locked while
    // the read runs, and an extracted save sends this text — never whatever
    // the box holds by then — so the Source text and the "Last analysed"
    // caption beside it can never describe two different pastes.
    const sent = text;
    setReadError(null);
    setReading(true);
    analyse.mutate(sent, {
      onSuccess: (result) => {
        if (attempt.current !== mine) return;
        const read: BiographyFieldValues = {};
        for (const f of BIOGRAPHY_FIELDS) {
          const value = result[f.key];
          read[f.key] = nonEmpty(value) ? value : null;
        }
        // 🔴 A read that found nothing is a FAILURE, whatever its status code.
        // Treated as a success it opened an empty form with a Save button —
        // and on a reviewed buyer, before this form started from the paste,
        // a form identical to the biography on file.
        if (!BIOGRAPHY_FIELDS.some((f) => nonEmpty(read[f.key]))) {
          setReadError(BIOGRAPHY_NOTHING_FOUND_MESSAGE);
          return;
        }
        setFields(read);
        setExtracted(read);
        setTouched(new Set());
        setKept(new Set());
        setProvenance({
          text: sent,
          model: result.modelUsed,
          rawJson: result.rawJson,
          analysedAtUtc: new Date().toISOString(),
          subjectNotice: nonEmpty(result.subjectNotice) ? result.subjectNotice : null,
        });
        setMode("extracted");
      },
      onError: (err: unknown) => {
        if (attempt.current !== mine) return;
        setReadError(analyseFailureMessage(err));
      },
      onSettled: () => {
        if (attempt.current === mine) setReading(false);
      },
    });
  };

  /** The way forward from a read that failed: the same form, starting from what
   *  is saved (or empty), labelled as a hand entry. */
  const fillByHand = () => {
    setReadError(null);
    setFields({ ...saved });
    setExtracted({});
    setTouched(new Set());
    setKept(new Set());
    setProvenance(null);
    setMode("byHand");
  };

  const typeInto = (key: BiographyFieldKey, value: string) => {
    setFields((current) => ({ ...current, [key]: value }));
    setTouched((current) => new Set(current).add(key));
    // A kept value somebody has since typed into is theirs, not the file's.
    setKept((current) => {
      if (!current.has(key)) return current;
      const next = new Set(current);
      next.delete(key);
      return next;
    });
  };

  const keepSaved = (key: BiographyFieldKey) => {
    setFields((current) => ({ ...current, [key]: saved[key] ?? null }));
    setKept((current) => new Set(current).add(key));
  };

  // What the bulk keep would fill: a field the paste is silent about, that
  // nobody has typed into, that is empty, and that has something on file.
  // Computed each render, so once it has filled them it is not offered again.
  const fillable =
    mode === "extracted"
      ? BIOGRAPHY_FIELDS.filter(
          ({ key }) =>
            !nonEmpty(extracted[key])
            && !touched.has(key)
            && !nonEmpty(fields[key])
            && nonEmpty(saved[key]),
        ).map((f) => f.key)
      : [];

  /** A one-time fill, not a mode: every field it fills is an ordinary field
   *  afterwards, labelled as kept until somebody types into it. */
  const keepUnmentioned = () => {
    const keys = fillable;
    setFields((current) => {
      const next = { ...current };
      for (const key of keys) next[key] = saved[key] ?? null;
      return next;
    });
    setKept((current) => new Set([...current, ...keys]));
  };

  const unmentionedOnFile =
    mode === "extracted"
    && BIOGRAPHY_FIELDS.some(({ key }) => !nonEmpty(extracted[key]) && nonEmpty(saved[key]));

  const allEmpty = !BIOGRAPHY_FIELDS.some((f) => nonEmpty(fields[f.key]));

  const runSave = () => {
    const mine = attempt.current;
    // Only a read that actually ran may stamp itself as one. A hand entry is a
    // human typing into a form.
    const analysis = mode === "extracted" ? provenance : null;
    save.mutate(
      buildPayload({
        values: fields,
        // An extracted save sends the text that was READ, with that read's
        // provenance. Otherwise a blank box leaves the source text alone rather
        // than clearing it.
        rawText: analysis ? analysis.text : nonEmpty(text) ? text : null,
        provenance: analysis,
      }),
      {
        onSuccess: (result) => {
          announceSave(toast, result);
          // The save still happened if the specialist cancelled and reopened
          // meanwhile — but the paste they have open now is not this one.
          if (attempt.current === mine) close();
        },
        onError: (e: unknown) => toast.error(extractErrorMessage(e)),
      },
    );
  };

  const title =
    mode === "extracted"
      ? "Review & save biography"
      : mode === "byHand"
        ? "Fill in the biography by hand"
        : "Paste a biography";

  return (
    <Modal
      open={open}
      onClose={close}
      title={title}
      size="lg"
      footer={
        mode === null ? (
          <>
            <Button variant="text" onClick={close}>
              Cancel
            </Button>
            <Button
              disabled={analyse.isPending || text.trim().length === 0}
              onClick={runAnalyse}
              data-testid="buyer-biography-analyse"
            >
              <Sparkles size={14} strokeWidth={1.5} aria-hidden />
              {analyse.isPending ? "Analysing…" : "Analyse"}
            </Button>
          </>
        ) : (
          <>
            {allEmpty && (
              <EmptyBiographyReason id={emptyReasonId} testId="buyer-biography-empty-reason" />
            )}
            <Button variant="text" onClick={close}>
              Cancel
            </Button>
            <Button
              disabled={allEmpty || save.isPending}
              aria-describedby={
                [
                  allEmpty ? emptyReasonId : null,
                  // The subject notice is the sentence to hear BEFORE saving. A
                  // status region inserted already holding its text is not
                  // reliably announced, so Save points at it as well.
                  mode === "extracted" && provenance?.subjectNotice ? subjectNoticeId : null,
                ]
                  .filter(Boolean)
                  .join(" ") || undefined
              }
              onClick={runSave}
              data-testid="buyer-biography-save"
            >
              {save.isPending ? "Saving…" : "Save and mark reviewed"}
            </Button>
          </>
        )
      }
    >
      {mode === null ? (
        <div className="grid gap-space-3">
          <p className="m-0 text-small text-gray-mid">
            {firstPaste ? (
              <>
                Paste the buyer&apos;s résumé, LinkedIn profile or biography. When you
                analyse it, the text you pasted is kept on the buyer so it isn&apos;t
                lost. Nothing else is saved until you review what the assistant read
                and press Save.
              </>
            ) : (
              <>
                Paste the buyer&apos;s résumé, LinkedIn profile or biography. Nothing is
                saved — not even the text you pasted — until you press Save. What the
                assistant reads lands in a form on the next step for you to check,
                change or clear.
              </>
            )}
          </p>
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            disabled={reading}
            rows={12}
            placeholder="Paste here…"
            aria-label="Paste a résumé or biography to extract"
            data-testid="buyer-biography-paste-text"
          />
          {readError && (
            <div className="grid justify-items-start gap-space-2">
              <p
                className="m-0 text-small text-status-error"
                role="alert"
                data-testid="buyer-biography-analyse-error"
              >
                {readError}
              </p>
              <Button
                variant="text"
                size="sm"
                onClick={fillByHand}
                data-testid="buyer-biography-by-hand"
              >
                Fill in the fields by hand instead
              </Button>
            </div>
          )}
        </div>
      ) : (
        <div className="grid gap-space-4">
          {mode === "extracted" ? (
            <div className="grid justify-items-start gap-space-2">
              {provenance?.subjectNotice && (
                <p
                  id={subjectNoticeId}
                  className="m-0 text-small text-status-warning-ink"
                  role="status"
                  data-testid="buyer-biography-subject-notice"
                >
                  {provenance.subjectNotice}
                </p>
              )}
              {/* The model identifier is deliberately not published to staff —
                  the same call already made on the buyer page and in the
                  listing paste modal. What the reader needs to know is that a
                  human did not type this. */}
              <p className="m-0 text-caption text-gray-mid">
                Extracted automatically from pasted text. Nothing here is saved until
                you press Save.
                {unmentionedOnFile
                  && " A field the paste doesn't mention starts empty, with what's on file shown under it — keep it, or it is cleared when you save."}
              </p>
              {fillable.length > 0 && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={keepUnmentioned}
                  data-testid="buyer-biography-keep-unmentioned"
                >
                  Keep everything the paste doesn&apos;t mention
                </Button>
              )}
            </div>
          ) : (
            <p className="m-0 text-caption text-gray-mid" data-testid="buyer-biography-hand-entry">
              You&apos;re filling this in by hand — nothing here was read by the
              assistant.
              {nonEmpty(text) && " When you save, the text you pasted is kept as the source text."}
            </p>
          )}

          {BIOGRAPHY_SECTIONS.map((section) => (
            <div key={section.id} className="grid gap-space-3">
              <h3 className="m-0 font-display text-label font-semibold uppercase tracking-label text-gold-shadow">
                {section.title}
              </h3>
              {section.fields.map((f) => (
                <ReviewField
                  key={f.key}
                  label={f.label}
                  fieldKey={f.key}
                  value={fields[f.key] ?? ""}
                  savedValue={saved[f.key] ?? null}
                  kept={kept.has(f.key)}
                  onType={(v) => typeInto(f.key, v)}
                  onKeep={() => keepSaved(f.key)}
                />
              ))}
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}

type PasteFormMode = "extracted" | "byHand";

/** The analysis a save carries — the read THIS paste ran, never the one on
 *  file, which described an earlier paste — and the text it read, which an
 *  extracted save sends as the Source text. */
interface BiographyProvenance {
  /** The text this read analysed, exactly as it was sent. */
  text: string;
  model: string | null;
  rawJson: string | null;
  analysedAtUtc: string;
  /** Whose background this read was — the server's sentence, shown above the
   *  form so a person checks it before saving. Dies with the read. */
  subjectNotice: string | null;
}

/**
 * One value, editable — and, when it differs from what is on file, what is on
 * file, with a way to keep it. A value the read would replace names the value
 * it replaces; an empty field names what saving it empty would clear.
 */
function ReviewField({
  label,
  fieldKey,
  value,
  savedValue,
  kept,
  onType,
  onKeep,
}: {
  label: string;
  fieldKey: BiographyFieldKey;
  value: string;
  savedValue: string | null;
  kept: boolean;
  onType: (value: string) => void;
  onKeep: () => void;
}) {
  const differs = nonEmpty(savedValue) && savedValue !== value;

  return (
    <label className="grid gap-space-1">
      <span className="text-caption uppercase tracking-wide text-gray-mid">{label}</span>
      <Textarea
        value={value}
        onChange={(e) => onType(e.target.value)}
        rows={2}
        aria-label={label}
        data-testid={`buyer-biography-review-${fieldKey}`}
      />
      {differs ? (
        <span className="flex flex-wrap items-baseline gap-space-2 text-caption text-status-warning-ink">
          {nonEmpty(value) ? (
            <span data-testid={`buyer-biography-replaces-${fieldKey}`}>
              Replaces what is on file: “{truncate(savedValue ?? "")}”
            </span>
          ) : (
            <span data-testid={`buyer-biography-onfile-${fieldKey}`}>
              On file: “{truncate(savedValue ?? "")}”
            </span>
          )}
          <Button
            variant="text"
            size="sm"
            onClick={onKeep}
            data-testid={`buyer-biography-keep-${fieldKey}`}
          >
            Keep what&apos;s saved
          </Button>
        </span>
      ) : kept ? (
        <span className="text-caption text-gray-mid" data-testid={`buyer-biography-kept-${fieldKey}`}>
          Kept from what&apos;s saved
        </span>
      ) : null}
    </label>
  );
}

/** One part of the document, in an editor. Saves only that part's fields. */
function BiographySectionEditModal({
  buyerLeadId,
  section,
  saved,
  onClose,
}: {
  buyerLeadId: string;
  section: BiographySection;
  saved: BiographyFieldValues;
  onClose: () => void;
}) {
  const toast = useToast();
  const save = useSaveBuyerBiography(buyerLeadId);
  const emptyReasonId = useId();
  const [fields, setFields] = useState<BiographyFieldValues>(saved);

  // Over all thirteen fields, not this section's: the save carries the whole
  // biography, and emptying the last field of it is what the server refuses.
  const allEmpty = !BIOGRAPHY_FIELDS.some((f) => nonEmpty(fields[f.key]));

  const runSave = () => {
    save.mutate(
      // 🔴 `rawText: null` — "leaves the source text alone", per the payload's
      // own contract. `""` CLEARS it, so a section editor that echoed a value it
      // never showed would be one careless default away from destroying the
      // evidence every extracted field is checked against.
      //
      // A hand edit sends NO analysis of its own. The server keeps the analysis
      // on file when the source text is untouched — it describes the last
      // ANALYSIS of that text, not the current field values — so correcting one
      // field neither erases it nor re-claims it.
      buildPayload({ values: fields, rawText: null, provenance: null }),
      {
        onSuccess: (result) => {
          announceSave(toast, result);
          onClose();
        },
        onError: (e: unknown) => toast.error(extractErrorMessage(e)),
      },
    );
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={`Edit ${section.title.toLowerCase()}`}
      size="md"
      footer={
        <>
          {allEmpty && (
            <EmptyBiographyReason
              id={emptyReasonId}
              testId={`buyer-biography-empty-reason-${section.id}`}
            />
          )}
          <Button variant="text" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={allEmpty || save.isPending}
            aria-describedby={allEmpty ? emptyReasonId : undefined}
            onClick={runSave}
            data-testid={`buyer-biography-save-${section.id}`}
          >
            {save.isPending ? "Saving…" : "Save and mark reviewed"}
          </Button>
        </>
      }
    >
      <div className="grid gap-space-3">
        {section.fields.map((f) => (
          <label key={f.key} className="grid gap-space-1">
            <span className="text-caption uppercase tracking-wide text-gray-mid">
              {f.label}
            </span>
            <Textarea
              value={fields[f.key] ?? ""}
              onChange={(e) => setFields((c) => ({ ...c, [f.key]: e.target.value }))}
              rows={3}
              aria-label={f.label}
              data-testid={`buyer-biography-edit-field-${f.key}`}
            />
          </label>
        ))}
      </div>
    </Modal>
  );
}

/**
 * Why Save is disabled on an empty biography, beside the button it disables.
 *
 * <p>🔴 Stamped reviewed, an empty biography would pass the package gate with
 * nothing in it — and a read that found nothing was exactly how one used to get
 * saved. The server refuses one too; this says so before anyone presses Save.</p>
 */
function EmptyBiographyReason({ id, testId }: { id: string; testId: string }) {
  return (
    <span id={id} className="mr-auto text-caption text-gray-mid" data-testid={testId}>
      {BIOGRAPHY_EMPTY_MESSAGE}
    </span>
  );
}

/* --------------------------------------------------------------- the plumbing */

/**
 * The save body.
 *
 * <p>Always carries all thirteen values, because the endpoint is a PUT upsert —
 * sending only the edited part of the document would blank the rest of it. A
 * blank value is sent as null, which is what the server stores it as.</p>
 *
 * <p>🔴 The analysis it carries is the one THIS save ran, or none. It used to
 * send back whatever analysis was on file, so replacing the source text without
 * analysing it left a "Last analysed" caption describing a paste nobody had
 * analysed.</p>
 */
function buildPayload({
  values,
  rawText,
  provenance,
}: {
  values: BiographyFieldValues;
  /** `null` leaves the source text alone; a string replaces it. */
  rawText: string | null;
  provenance: BiographyProvenance | null;
}): UpsertBuyerLeadBiographyPayload {
  const out: UpsertBuyerLeadBiographyPayload = {
    rawText,
    aiModel: provenance?.model ?? null,
    aiRawJson: provenance?.rawJson ?? null,
    aiAnalysedAtUtc: provenance?.analysedAtUtc ?? null,
  };
  for (const f of BIOGRAPHY_FIELDS) {
    const value = values[f.key];
    out[f.key] = nonEmpty(value) ? value : null;
  }
  return out;
}

/**
 * Says what a save did, in the SERVER's account of it.
 *
 * <p>A response with no outcome is from a server that always saved and always
 * re-stamped the review, which is what "saved and marked reviewed" says.</p>
 */
function announceSave(toast: ReturnType<typeof useToast>, result: BuyerLeadBiographyDto) {
  const outcome = result.saveOutcome ?? "FieldsChanged";
  const message = BIOGRAPHY_SAVE_ANNOUNCEMENTS[outcome];
  if (outcome === "Unchanged") toast.info(message);
  else toast.success(message);
}

/**
 * What a failed read says.
 *
 * <p>A 422 carries the sentence the server wrote for the specialist — one of the
 * six named reasons, the daily AI limit among them. A 429 from the rate limiter
 * has no body and keeps the handling it always had: the client's own status
 * line. A 5xx or a network failure carries nothing a specialist can act on, so
 * it gets one plain sentence instead of the server's log text.</p>
 */
function analyseFailureMessage(err: unknown): string {
  return err instanceof ApiError && err.status < 500
    ? extractErrorMessage(err)
    : BIOGRAPHY_ANALYSE_FAILED_MESSAGE;
}

function valuesOf(biography?: BuyerLeadBiographyDto | null): BiographyFieldValues {
  const out: BiographyFieldValues = {};
  for (const f of BIOGRAPHY_FIELDS) out[f.key] = biography?.[f.key] ?? null;
  return out;
}

function nonEmpty(value: string | null | undefined): value is string {
  return value != null && value.trim().length > 0;
}

/** Enough of a saved value to recognise it by, never the whole essay. */
function truncate(value: string, max = 80): string {
  const flat = value.replace(/\s+/g, " ").trim();
  return flat.length > max ? `${flat.slice(0, max)}…` : flat;
}
